/**
 * Autodiscovery — mail client autoconfig per doména
 *
 * UI: list domén + edit modal + sync from stalwart + publish/unpublish.
 * Vzor: webhost.tsx (CRUD list + modal pattern).
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Cloud,
  Download,
  Edit2,
  Mail,
  Plus,
  RefreshCw,
  Smartphone,
  TestTube,
  Trash2,
  Upload,
} from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Card,
  CardContent,
  EmptyState,
  Input,
  Label,
  Modal,
  PageHeader,
  Select,
  StatCard,
  StatsGrid,
} from '../../components/ui';
import { api } from '../../lib/api';
import { themedConfirm } from '../../lib/themed-dialogs';

// ── Types ───────────────────────────────────────────────────────────────────

interface Config {
  domain: string;
  source: 'manual' | 'stalwart' | 'imported';
  display_name: string | null;
  imap_host: string;
  imap_port: number;
  imap_socket: 'SSL' | 'STARTTLS' | 'plain';
  smtp_host: string;
  smtp_port: number;
  smtp_socket: 'SSL' | 'STARTTLS' | 'plain';
  pop3_host: string | null;
  pop3_port: number | null;
  pop3_socket: string | null;
  apple_profile_enabled: boolean;
  dns_sync_enabled: boolean;
  last_synced: string | null;
  updated_at: string;
}

// ── Edit Modal ──────────────────────────────────────────────────────────────

function EditConfigModal({
  open,
  initial,
  onClose,
  onSaved,
}: {
  open: boolean;
  initial: Partial<Config> | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const isEdit = Boolean(initial?.domain);
  const [domain, setDomain] = useState(initial?.domain ?? '');
  const [displayName, setDisplayName] = useState(initial?.display_name ?? '');
  const [imapHost, setImapHost] = useState(initial?.imap_host ?? '');
  const [imapPort, setImapPort] = useState(String(initial?.imap_port ?? 993));
  const [imapSocket, setImapSocket] = useState(initial?.imap_socket ?? 'SSL');
  const [smtpHost, setSmtpHost] = useState(initial?.smtp_host ?? '');
  const [smtpPort, setSmtpPort] = useState(String(initial?.smtp_port ?? 465));
  const [smtpSocket, setSmtpSocket] = useState(initial?.smtp_socket ?? 'SSL');
  const [pop3Host, setPop3Host] = useState(initial?.pop3_host ?? '');
  const [pop3Port, setPop3Port] = useState(String(initial?.pop3_port ?? ''));
  const [pop3Socket, setPop3Socket] = useState(initial?.pop3_socket ?? 'SSL');
  const [appleEnabled, setAppleEnabled] = useState(Boolean(initial?.apple_profile_enabled));
  const [dnsSyncEnabled, setDnsSyncEnabled] = useState(Boolean(initial?.dns_sync_enabled));

  const mut = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = {
        domain: domain.trim(),
        display_name: displayName.trim() || null,
        imap_host: imapHost.trim(),
        imap_port: Number(imapPort),
        imap_socket: imapSocket,
        smtp_host: smtpHost.trim(),
        smtp_port: Number(smtpPort),
        smtp_socket: smtpSocket,
        apple_profile_enabled: appleEnabled,
        dns_sync_enabled: dnsSyncEnabled,
      };
      if (pop3Host.trim()) {
        body.pop3_host = pop3Host.trim();
        body.pop3_port = Number(pop3Port);
        body.pop3_socket = pop3Socket;
      }
      return api.mcp<{ ok: boolean; errors?: string[]; error?: string }>(
        'autodiscovery.upsert',
        body,
      );
    },
    onSuccess: (r) => {
      if (r.ok) {
        toast.success(t('autodiscovery.configSaved', { domain }));
        onSaved();
        onClose();
      } else {
        toast.error(r.errors?.join(', ') ?? r.error ?? t('autodiscovery.saveError'));
      }
    },
    onError: (e: Error) => toast.error(t('autodiscovery.errorPrefix', { message: e.message })),
  });

  if (!open) return null;

  return (
    <Modal open={open} onClose={onClose} title={isEdit ? t('autodiscovery.editDomain', { domain }) : t('autodiscovery.addDomain')}>
      <div className="space-y-3">
        <div>
          <Label>{t('autodiscovery.domainLabel')}</Label>
          <Input
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            placeholder="firma.cz"
            disabled={isEdit}
          />
        </div>
        <div>
          <Label>{t('autodiscovery.displayNameLabel')}</Label>
          <Input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Firma s.r.o."
          />
        </div>

        <div className="border-t border-zinc-800 pt-3 mt-3">
          <div className="text-xs uppercase opacity-60 mb-2">{t('autodiscovery.imapSection')}</div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label>Host</Label>
              <Input value={imapHost} onChange={(e) => setImapHost(e.target.value)} placeholder="mail.firma.cz" />
            </div>
            <div>
              <Label>Port</Label>
              <Input value={imapPort} onChange={(e) => setImapPort(e.target.value)} type="number" />
            </div>
            <div>
              <Label>Socket</Label>
              <Select
                value={imapSocket}
                onChange={(e) => setImapSocket(e.target.value as 'SSL' | 'STARTTLS' | 'plain')}
              >
                <option value="SSL">SSL/TLS</option>
                <option value="STARTTLS">STARTTLS</option>
                <option value="plain">Plain</option>
              </Select>
            </div>
          </div>
        </div>

        <div>
          <div className="text-xs uppercase opacity-60 mb-2">{t('autodiscovery.smtpSection')}</div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label>Host</Label>
              <Input value={smtpHost} onChange={(e) => setSmtpHost(e.target.value)} placeholder="mail.firma.cz" />
            </div>
            <div>
              <Label>Port</Label>
              <Input value={smtpPort} onChange={(e) => setSmtpPort(e.target.value)} type="number" />
            </div>
            <div>
              <Label>Socket</Label>
              <Select
                value={smtpSocket}
                onChange={(e) => setSmtpSocket(e.target.value as 'SSL' | 'STARTTLS' | 'plain')}
              >
                <option value="SSL">SSL/TLS (465)</option>
                <option value="STARTTLS">STARTTLS (587)</option>
                <option value="plain">Plain</option>
              </Select>
            </div>
          </div>
        </div>

        <details className="border-t border-zinc-800 pt-2">
          <summary className="text-xs uppercase opacity-60 cursor-pointer">{t('autodiscovery.pop3Section')}</summary>
          <div className="grid grid-cols-3 gap-2 mt-2">
            <Input value={pop3Host} onChange={(e) => setPop3Host(e.target.value)} placeholder="pop3.firma.cz" />
            <Input value={pop3Port} onChange={(e) => setPop3Port(e.target.value)} type="number" placeholder="995" />
            <Select value={pop3Socket} onChange={(e) => setPop3Socket(e.target.value)}>
              <option value="SSL">SSL/TLS</option>
              <option value="STARTTLS">STARTTLS</option>
              <option value="plain">Plain</option>
            </Select>
          </div>
        </details>

        <div className="flex gap-4 pt-2">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={appleEnabled}
              onChange={(e) => setAppleEnabled(e.target.checked)}
            />
            Apple .mobileconfig (iOS Mail)
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={dnsSyncEnabled}
              onChange={(e) => setDnsSyncEnabled(e.target.checked)}
            />
            DNS SRV sync
          </label>
        </div>

        <div className="flex justify-end gap-2 pt-3">
          <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            onClick={() => mut.mutate()}
            disabled={!domain || !imapHost || !smtpHost || mut.isPending}
          >
            {mut.isPending ? t('autodiscovery.saving') : isEdit ? t('common.save') : t('autodiscovery.create')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ── Main Page ───────────────────────────────────────────────────────────────

export default function AutodiscoveryPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const [editInitial, setEditInitial] = useState<Partial<Config> | null>(null);

  const list = useQuery<Config[]>({
    queryKey: ['autodiscovery-configs'],
    queryFn: async () => {
      const r = await api.mcp<{ configs: Config[] }>('autodiscovery.list', {});
      return r.configs ?? [];
    },
  });

  const syncMut = useMutation({
    mutationFn: () =>
      api.mcp<{ ok: boolean; created?: number; refreshed?: number; error?: string }>(
        'autodiscovery.sync_from_stalwart',
        {},
      ),
    onSuccess: (r) => {
      if (r.ok) {
        toast.success(t('autodiscovery.syncOk', { created: r.created ?? 0, refreshed: r.refreshed ?? 0 }));
        qc.invalidateQueries({ queryKey: ['autodiscovery-configs'] });
      } else {
        toast.error(r.error ?? 'Sync failed');
      }
    },
    onError: (e: Error) => toast.error(t('autodiscovery.stalwartSyncError', { message: e.message })),
  });

  async function publishDomain(domain: string) {
    const r = await api.mcp<{ ok: boolean; error?: string }>('autodiscovery.publish', { domain });
    if (r.ok) toast.success(t('autodiscovery.published', { domain }));
    else toast.error(r.error ?? 'Publish failed');
    qc.invalidateQueries({ queryKey: ['autodiscovery-configs'] });
  }

  async function unpublishDomain(domain: string) {
    const r = await api.mcp<{ ok: boolean; error?: string }>('autodiscovery.unpublish', { domain });
    if (r.ok) toast.success(`Unpublished ${domain}`);
    else toast.error(r.error ?? 'Unpublish failed');
    qc.invalidateQueries({ queryKey: ['autodiscovery-configs'] });
  }

  async function testDomain(domain: string) {
    const r = await api.mcp<{
      ok: boolean;
      checks?: Record<string, { ok: boolean; error?: string }>;
    }>('autodiscovery.test', { domain });
    if (r.ok) {
      toast.success(t('autodiscovery.testAllOk', { domain }));
    } else {
      const failed = Object.entries(r.checks ?? {})
        .filter(([, v]) => !v.ok)
        .map(([k, v]) => `${k}: ${v.error ?? 'fail'}`)
        .join(', ');
      toast.error(`${domain}: ${failed || 'test failed'}`);
    }
  }

  async function dnsSyncDomain(domain: string) {
    const r = await api.mcp<{ ok: boolean; error?: string }>('autodiscovery.dns_sync', { domain });
    if (r.ok) toast.success(t('autodiscovery.dnsSyncOk', { domain }));
    else toast.error(r.error ?? 'DNS sync failed');
  }

  async function deleteDomain(domain: string) {
    if (!(await themedConfirm(t('autodiscovery.deleteConfirm', { domain })))) return;
    const r = await api.mcp<{ ok: boolean; error?: string }>('autodiscovery.delete', { domain });
    if (r.ok) toast.success(t('autodiscovery.deleted', { domain }));
    else toast.error(r.error ?? 'Delete failed');
    qc.invalidateQueries({ queryKey: ['autodiscovery-configs'] });
  }

  const configs = list.data ?? [];
  const stats = {
    total: configs.length,
    manual: configs.filter((c) => c.source === 'manual').length,
    stalwart: configs.filter((c) => c.source === 'stalwart').length,
    apple: configs.filter((c) => c.apple_profile_enabled).length,
  };

  return (
    <div>
      <PageHeader
        icon={<Mail size={20} />}
        title="Autodiscovery"
        subtitle={t('autodiscovery.subtitle')}
        actions={
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => syncMut.mutate()} disabled={syncMut.isPending}>
              <Download size={14} />
              {syncMut.isPending ? t('autodiscovery.syncing') : t('autodiscovery.syncFromStalwart')}
            </Button>
            <Button variant="primary" onClick={() => { setEditInitial(null); setEditOpen(true); }}>
              <Plus size={14} /> {t('autodiscovery.addDomain')}
            </Button>
          </div>
        }
      />

      <div className="p-4 space-y-4">
        <StatsGrid>
          <StatCard label={t('autodiscovery.totalDomains')} value={stats.total} />
          <StatCard label={t('autodiscovery.fromStalwart')} value={stats.stalwart} />
          <StatCard label={t('autodiscovery.manual')} value={stats.manual} />
          <StatCard label="Apple profile" value={stats.apple} />
        </StatsGrid>

        <Card>
          <CardContent>
            {configs.length === 0
              ? (
                <EmptyState
                  icon={<Mail size={32} />}
                  title={t('autodiscovery.noDomains')}
                  description={t('autodiscovery.noDomainsDesc')}
                />
              )
              : (
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase opacity-60 border-b border-zinc-800">
                    <tr>
                      <th className="text-left py-2">{t('autodiscovery.colDomain')}</th>
                      <th className="text-left">{t('autodiscovery.colSource')}</th>
                      <th className="text-left">IMAP</th>
                      <th className="text-left">SMTP</th>
                      <th className="text-left">Features</th>
                      <th className="text-right">{t('autodiscovery.colActions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {configs.map((c) => (
                      <tr key={c.domain} className="border-b border-zinc-800/50 hover:bg-zinc-900/40">
                        <td className="py-2 font-mono">{c.domain}</td>
                        <td>
                          <Badge variant={c.source === 'manual' ? 'blue' : 'green'}>{c.source}</Badge>
                        </td>
                        <td className="font-mono text-xs">
                          {c.imap_host}:{c.imap_port} <span className="opacity-50">{c.imap_socket}</span>
                        </td>
                        <td className="font-mono text-xs">
                          {c.smtp_host}:{c.smtp_port} <span className="opacity-50">{c.smtp_socket}</span>
                        </td>
                        <td>
                          {c.apple_profile_enabled && <Badge variant="purple"><Smartphone size={10} /> Apple</Badge>}
                          {c.dns_sync_enabled && <Badge variant="warning"><Cloud size={10} /> DNS</Badge>}
                        </td>
                        <td className="text-right space-x-1">
                          <Button variant="ghost" onClick={() => testDomain(c.domain)} title="Test"><TestTube size={12} /></Button>
                          <Button variant="ghost" onClick={() => publishDomain(c.domain)} title="Publish"><Upload size={12} /></Button>
                          <Button variant="ghost" onClick={() => unpublishDomain(c.domain)} title="Unpublish"><RefreshCw size={12} /></Button>
                          {c.dns_sync_enabled && (
                            <Button variant="ghost" onClick={() => dnsSyncDomain(c.domain)} title="DNS SRV sync"><Cloud size={12} /></Button>
                          )}
                          <Button variant="ghost" onClick={() => { setEditInitial(c); setEditOpen(true); }} title="Edit"><Edit2 size={12} /></Button>
                          <Button variant="danger" onClick={() => deleteDomain(c.domain)} title="Delete"><Trash2 size={12} /></Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
          </CardContent>
        </Card>
      </div>

      <EditConfigModal
        open={editOpen}
        initial={editInitial}
        onClose={() => { setEditOpen(false); setEditInitial(null); }}
        onSaved={() => qc.invalidateQueries({ queryKey: ['autodiscovery-configs'] })}
      />
    </div>
  );
}
